import React from 'react';
import PropTypes from 'prop-types';

import { useClassName } from '@travpro/hooks';
import { renderToString } from 'react-dom/server';
import { HashLink as Link } from 'react-router-hash-link';

import LinkHandler from 'components/LinkHandler';
import SharingPopup from 'components/SharingPopup';

import './_SearchResult.scss';
import { baseAppId } from 'settings';

// Skeleton loading component
const SkeletonLoader = ({ type }) => {
  const c = useClassName('search-result');
  return (
    <div {...c({ skeleton: true, [type]: true })}>
      <div {...c('skeleton-background')} />
      <div {...c('skeleton-content')}>
        <div {...c('skeleton-title')} />
        <div {...c('skeleton-description')} />
      </div>
    </div>
  );
};

SkeletonLoader.propTypes = {
  type: PropTypes.string,
};

/**
 * @typedef {object} SearchResultProps
 * @property {string} title Title to display
 * @property {string} icon Icon to display
 * @property {string} backgroundImage Background image to display
 * @property {boolean} gradient Whether to display a gradient over the background image
 * @property {string} url URL to link
 * @property {Function} onSelect Function to call on select
 */

/**
 * @param {SearchResultProps} props SearchResultProps
 * @returns {JSX.Element} SearchResult
 */
const SearchResult = ({
  appName = '',
  title = null,
  description = null,
  icon = null,
  backgroundImage = null,
  gradient = true,
  url = '',
  type = '',
  hitType = '',
  itemID = '',
  onSelect = () => {},
}) => {
  const c = useClassName('search-result');

  const checkedUrl = url || '#';
  const fancyOptions = {
    infobar: true,
    thumbs: {
      autoStart: true,
      axis: 'x',
    },
    baseClass: 'customBox',
    buttons: ['thumbs', 'share', 'close'],
    share: {
      tpl: renderToString(<SharingPopup url={checkedUrl} image={backgroundImage} />),
    },
  };

  const fancyOptionsShowcase = {
    infobar: true,
    thumbs: {
      autoStart: true,
      axis: 'x',
    },
    baseClass: 'customBox',
    buttons: ['close'],
  };
  // Show skeleton while image is loading - REMOVED to fix image loading issue
  // if (backgroundImage && !imageLoaded && !imageError) {
  //   return <SkeletonLoader type={type} />;
  // }

  if (type === 'sales_companion_items') {
    return (
      <span onClick={onSelect} role="button" tabIndex="0" onKeyDown={onSelect}>
        <LinkHandler
          appId={baseAppId}
          url={checkedUrl}
          type={hitType}
          title={title}
          UniqueID={checkedUrl}
          dataOptions={JSON.stringify(fancyOptions)}
          dataFancybox={checkedUrl}
          dataTrigger={checkedUrl}
          page="f"
        >
          <div {...c({ small: true })}>
            {icon && <img {...c('icon', { small: !backgroundImage })} src={icon} alt={`icon: ${title}`} />}

            {title && <p {...c('title', { small: !backgroundImage })}>{title}</p>}

            {backgroundImage && (
              <img 
                {...c('background')} 
                src={backgroundImage} 
                alt={`background: ${title}`}
              />
            )}

            {gradient === true && backgroundImage && <div {...c('gradient')} />}
          </div>
        </LinkHandler>
      </span>
    );
  }

  if (type === 'partner_pages') {
    return (
      <a onClick={onSelect} href={checkedUrl} >
        <div {...c( { partner: true })}>
          {backgroundImage && (
            <img 
              {...c('background')} 
              src={backgroundImage} 
              alt={`background: ${title}`}
            />
          )}
          {!backgroundImage && <div {...c('background', 'background--fallback')} />}

          {appName && <p {...c('title', { small: !backgroundImage })}>{appName}</p>}

          {title && <p {...c('subTitle', { small: !backgroundImage })}>{title}</p>}
          {description && <p {...c('description', { small: !backgroundImage })}>{description}</p>}

          {icon && <img {...c('icon', { small: !backgroundImage })} src={icon} alt="search-result icon" />}
        </div>
      </a>)
  }

  if (type === 'exhibition_tiles') {
    return (
      <div 
        onClick={onSelect} 
        data-src={checkedUrl}   
        data-type="iframe"
        data-fancybox={itemID}
        data-options={JSON.stringify(fancyOptionsShowcase)}
        data-fancybox-trigger={itemID}
        >
        <div {...c({ small: true })}>
          {backgroundImage && (
            <img 
              {...c('background')} 
              src={backgroundImage} 
              alt={`background: ${title}`}
            />
          )}
          {!backgroundImage && <div {...c('background', 'background--fallback')} />}

          {appName && <p {...c('title', { small: !backgroundImage })}>{appName}</p>}

          {title && <p {...c('subTitle', { small: !backgroundImage })}>{title}</p>}
          {description && <p {...c('description', { small: !backgroundImage })}>{description}</p>}

        </div>
      </div>)
  }

  return (
    <Link to={checkedUrl} onClick={onSelect}>
      <div {...c({ small: !backgroundImage })}>
        {backgroundImage && (
          <img 
            {...c('background')} 
            src={backgroundImage} 
            alt={`background: ${title}`}
          />
        )}
        {!backgroundImage && <div {...c('background', 'background--fallback')} />}
        {gradient === true && backgroundImage && <div {...c('gradient')} />}

        {title && <p {...c('title', { small: !backgroundImage })}>{title}</p>}

        {description && description.props.hit.text.length > 10 && (
          <p {...c('description', { small: !backgroundImage })}>{description}</p>
        )}

        {icon && <img {...c('icon', { small: !backgroundImage })} src={icon} alt="search-result icon" />}
      </div>
    </Link>
  );
};

export default SearchResult;

// Title and description can be either a string or an element. This is to allow for more complex search results, such as highlighted text, using Algolia Search components
SearchResult.propTypes = {
  appName: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  icon: PropTypes.string,
  backgroundImage: PropTypes.string,
  gradient: PropTypes.bool,
  url: PropTypes.string,
  onSelect: PropTypes.func,
  type: PropTypes.string,
  hitType: PropTypes.string,
  itemID: PropTypes.string,
};
